import familytPraying from "assets/family-praying.jpg";
import { useAuth } from "provider/AuthProvider";
import { sendPasswordResetEmail, signInWithEmailAndPassword } from "firebase/auth";
import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "stories/Loading/Loading";
import * as Yup from "yup";
import { ReactComponent as Icon } from "../../assets/app-icon-ios.svg";
import { auth } from "firebase.js";
import useFetch from "hooks/useFetch";
import styles from "./Login.module.scss";

const validationSchema = Yup.object({
  username: Yup.string().required("E-post er påkrevd"),
  password: Yup.string().required("Passord er påkrevd"),
});

const Login: React.FC = () => {
  const { execute, error, isLoading } = useFetch<{ admin: boolean }>();
  const navigate = useNavigate();
  const { setOrgNumber, setUid } = useAuth();
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  const handleForgotPassword = async (username: string) => {
    const email = username.trim();
    setGeneralError(null);
    if (!email) {
      setResetMessage("Skriv inn e-postadressen din i feltet over først.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      // Same message whether or not the account exists, so the form can't be used to probe emails
      setResetMessage(
        `Hvis ${email} har en konto, er det sendt en lenke for å lage nytt passord. Sjekk også søppelpost.`,
      );
    } catch (err: any) {
      if (err?.code === "auth/invalid-email") {
        setResetMessage("Skriv inn en gyldig e-postadresse.");
      } else if (err?.code === "auth/user-not-found") {
        setResetMessage(
          `Hvis ${email} har en konto, er det sendt en lenke for å lage nytt passord. Sjekk også søppelpost.`,
        );
      } else {
        setResetMessage("Kunne ikke sende e-post akkurat nå. Prøv igjen senere.");
      }
    }
  };

  const handleLogin = async (
    values: { username: string; password: string },
    setError: (errorMessage: string) => void,
  ) => {
    try {
      const email = values.username.trim();
      const password = values.password.trim();

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const idToken = await userCredential.user.getIdToken();

      sessionStorage.setItem("token", idToken);

      // Same check as the admin endpoints in the iFaste backend (iBønn iFaste verifyToken + admin permission)
      const data = await execute("/api/v1/users/admin-access", "GET");

      if (data?.admin) {
        const uid = userCredential.user.uid;

        setUid(uid);
        setOrgNumber("ifaste_admin");

        // Save details to session storage
        sessionStorage.setItem("uid", uid);
        sessionStorage.setItem("orgNumber", "ifaste_admin");

        navigate("/");
      } else {
        sessionStorage.removeItem("token");
        setError("Kontoen har ikke tilgang til iFaste Admin.");
      }
    } catch (err: any) {
      const errorCode = err?.code || "unknown";
      const friendlyMessage = getFriendlyErrorMessage(errorCode);
      setError(friendlyMessage);
    }
  };

  const getFriendlyErrorMessage = (errorCode: string) => {
    switch (errorCode) {
      case "auth/invalid-email":
        return "Please enter a valid email.";
      case "auth/invalid-password":
        return "Incorrect password. Please try again.";
      case "auth/invalid-credential":
        return "Invalid email or password. Please check your credentials and try again.";
      case "auth/user-not-found":
        return "No account found with this email address.";
      case "auth/too-many-requests":
        return "Too many failed login attempts. Please try again later.";
      default:
        return "An error occurred. Please try again.";
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.leftSection}>
        <img
          src={familytPraying}
          alt="Family praying at sunset in a mosque"
          className="img-fluid"
        />
      </div>
      <div className={styles.rightSection}>
        <Icon className={styles.icon} />
        <hr />
        <h1 className="mb-5">Velkommen til iFaste Admin</h1>
        {isLoading ? (
          <Loading size="large" variant="primary" message="Logger inn..." />
        ) : (
          <Formik
            initialValues={{ username: "", password: "" }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              handleLogin(values, (errorMessage: string) => {
                setGeneralError(errorMessage);
                setSubmitting(false);
              });
            }}
          >
            {({ isSubmitting, values }) => (
              <Form className="w-75">
                <div className={styles.formControl}>
                  <label htmlFor="username" className={styles.formLabel}>
                    E-post
                  </label>
                  <Field
                    type="email"
                    id="username"
                    name="username"
                    autoComplete="username"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="username"
                    component="div"
                    className={styles.errorMessage}
                  />
                </div>
                <div className={styles.formControl}>
                  <label htmlFor="password" className={styles.formLabel}>
                    Passord
                  </label>
                  <Field
                    type="password"
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="password"
                    component="div"
                    className={styles.errorMessage}
                  />
                </div>

                <button
                  type="submit"
                  className={`btn btn-primary ${styles.btnPrimary}`}
                  disabled={isSubmitting || isLoading}
                >
                  Logg inn
                </button>

                <button
                  type="button"
                  className={`btn btn-link ${styles.forgotPassword}`}
                  onClick={() => handleForgotPassword(values.username)}
                >
                  Glemt passord?
                </button>

                {resetMessage && (
                  <div role="status" className={styles.infoMessage}>
                    {resetMessage}
                  </div>
                )}

                {generalError ? (
                  <div className={styles.errorMessage}>{generalError}</div>
                ) : error ? (
                  <div className={styles.errorMessage}>
                    {error.text || "En feil oppstod"}
                  </div>
                ) : null}
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};

export default Login;
